import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useRegistrRidicuContext } from '@gov-nx/module/page';
import { ServiceCode } from '@gov-nx/module/service';
import {
	DataTable,
	Dialog,
	InfoBanner,
	Space,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { useWindowDimensions } from '@gov-nx/utils/web';
import { serviceRouteResolver } from '@gov-nx/web';

const MOBILE_WIDTH_MAX = 768;

export interface DriversRegistryOffencesViewProps {
	code: PageCode;
}

export const DriversRegistryOffencesView = ({
	code,
}: DriversRegistryOffencesViewProps) => {
	const { data, selectedOffence, selectOffence } = useRegistrRidicuContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { width } = useWindowDimensions();
	const isMobile = width < MOBILE_WIDTH_MAX;
	/* @TODO color points from one source getDriversPointsColor */
	const statusClassName =
		data?.points?.state === 'good'
			? 'gov-color--text-status-success'
			: data?.points?.state === 'warning'
				? 'gov-color--text-status-warning'
				: data?.points?.state === 'bad'
					? 'gov-color--text-status-error'
					: '';
	return (
		<div>
			<section className="flex flex-col gap-6">
				<Tiles>
					<Tile
						to={serviceRouteResolver(
							ServiceCode['vypis-bodoveho-hodnoceni-ridice']
						)}
						name={ts('vypis-bodoveho-hodnoceni-ridice')}
						icon={{ name: 'chevron-right', type: 'components' }}
						customClasses="[ last-of-type:border-b ]">
						<p className="text-(--text-secondary)">
							{ts('aktualni-pocet-bodu')}:{' '}
							<strong className={statusClassName}>
								{data?.points?.current}
							</strong>{' '}
							{ts('bodu-z')} {data?.points?.max}
						</p>
					</Tile>
				</Tiles>

				<section className="py-2">
					<h2 className="text-headline-s!">{ts('prestupky')}</h2>
					{data?.hasOffences ? (
						<Tiles>
							{data?.offences?.map((offence, index) => {
								const name = isMobile
									? `${
											offence.offenceDate
												? toDateReadable(offence.offenceDate)
												: ts('data.neurceno')
										}`
									: `${offence.sanctions.join(', ')} + ${offence.points} ${ts(
											'bodu'
										)}`;
								const content = isMobile ? (
									<>
										{ts('Sankce')}:<Space />
										<strong>{offence.sanctions.join(', ')}</strong>
										<Space />
										{ts('Body')}: <strong>{offence.points}</strong>
									</>
								) : (
									<>
										{ts('datum')}:<Space />
										{offence.offenceDate
											? toDateReadable(offence.offenceDate)
											: ts('data.neurceno')}
									</>
								);

								return (
									<Tile
										key={offence.id}
										onClick={() => selectOffence(index)}
										name={name}
										icon={{ name: 'chevron-right', type: 'components' }}>
										<p className="text-(--text-secondary)">{content}</p>
									</Tile>
								);
							})}
						</Tiles>
					) : (
						<InfoBanner
							headline={ts('stav.chyba.zadne-prestupky')}
							text={ts('stav.chyba.zadne-prestupky-popis')}
						/>
					)}
				</section>
			</section>

			<Dialog
				isOpen={!!selectedOffence}
				onClose={() => selectOffence()}
				title={<h3>{ts('prestupky')}</h3>}>
				<DataTable responsive>
					<tbody>
						{selectedOffence?.offenceDate && (
							<tr>
								<td className="sm:w-1/2">{ts('data.datum-spachani')}</td>
								<td className="sm:w-1/2">
									<strong>{toDateReadable(selectedOffence.offenceDate)}</strong>
								</td>
							</tr>
						)}
						{selectedOffence?.decisionDate && (
							<tr>
								<td>{ts('data.rozhodnuti-nabylo-pravni-moci')}</td>
								<td>
									<strong>
										{toDateReadable(selectedOffence.decisionDate)}
									</strong>
								</td>
							</tr>
						)}
						{selectedOffence?.decisionBureau && (
							<tr>
								<td>{ts('data.rozhodl')}</td>
								<td>
									<strong>{selectedOffence.decisionBureau}</strong>
								</td>
							</tr>
						)}
						{selectedOffence?.sanctions &&
							selectedOffence.sanctions.length > 0 && (
								<tr>
									<td>{ts('data.duvod')}</td>
									<td>
										<strong>{selectedOffence.sanctions.join(', ')}</strong>
									</td>
								</tr>
							)}
						{selectedOffence?.violation && (
							<tr>
								<td>{ts('data.porusena-ustanoveni')}</td>
								<td>
									<strong>{selectedOffence.violation}</strong>
								</td>
							</tr>
						)}
						{selectedOffence?.offence && (
							<tr>
								<td>{ts('data.spachane-prestupky')}</td>
								<td>
									<strong>{selectedOffence.offence}</strong>
								</td>
							</tr>
						)}
						{selectedOffence?.points !== undefined && (
							<tr>
								<td>{ts('data.body')}</td>
								<td>
									<strong>{selectedOffence.points}</strong>
								</td>
							</tr>
						)}
					</tbody>
				</DataTable>
			</Dialog>
		</div>
	);
};

DriversRegistryOffencesView.displayName = 'DriversRegistryOffencesView';
